/**
 * /frontend/src/features/analytics/utils/chartDataTransformers.js
 */

import { asNumber, asText } from './formatters';

/**
 * Builds line chart series points for queue wait trends.
 * @param {any} trend
 * @returns {Array<{label:string,avgWaitMinutes:number,medianWaitMinutes:number}>}
 */
export const toQueueWaitTrendSeries = (trend) => (trend?.points || []).map((point) => ({
  label: asText(point?.label, ''),
  avgWaitMinutes: asNumber(point?.avgWaitMinutes),
  medianWaitMinutes: asNumber(point?.medianWaitMinutes)
}));

/**
 * Maps throughput-by-hour for bar chart.
 * @param {any} performance
 * @returns {Array<{hourLabel:string,count:number}>}
 */
export const toThroughputByHourSeries = (performance) => (performance?.throughputByHour || []).map((item) => ({
  hourLabel: asText(item?.hourLabel, String(item?.hour ?? '')),
  count: asNumber(item?.count)
}));

/**
 * Maps service time per section for bar chart.
 * @param {any} performance
 * @returns {Array<{sectionName:string,avgServiceMinutes:number}>}
 */
export const toServiceTimeBySectionSeries = (performance) => (performance?.serviceTimeBySection || []).map((item) => ({
  sectionName: asText(item?.sectionName, item?.sectionUuid || ''),
  avgServiceMinutes: asNumber(item?.avgServiceMinutes)
}));

/**
 * Maps outcome distribution to pie chart data.
 * @param {any} performance
 * @returns {Array<{name:string,value:number}>}
 */
export const toOutcomePieSeries = (performance) => (performance?.outcomeDistribution || []).map((item) => ({
  name: asText(item?.outcome, ''),
  value: asNumber(item?.count)
}));

/**
 * Maps peak heatmap matrix into HeatmapGrid rows.
 * @param {any} heatmap
 * @returns {Array<{dayLabel:string,cells:Array<{hour:number,joinCount:number,avgWaitMinutes:number}>}>}
 */
export const toHeatmapRows = (heatmap) => (heatmap?.matrix || []).map((row) => ({
  dayLabel: asText(row?.[0]?.dayLabel, ''),
  cells: (row || []).map((cell) => ({
    hour: asNumber(cell?.hour),
    joinCount: asNumber(cell?.joinCount),
    avgWaitMinutes: asNumber(cell?.avgWaitMinutes),
    dayIndex: asNumber(cell?.dayIndex),
    dayLabel: asText(cell?.dayLabel, '')
  }))
}));

/**
 * Maps peak top-hours response into bar series.
 * @param {any} topHours
 * @returns {Array<{label:string,totalJoins:number}>}
 */
export const toPeakTopHoursSeries = (topHours) => (topHours?.topHours || []).map((item) => ({
  label: asText(item?.bucket, `${asText(item?.dayLabel)} ${asText(item?.hourLabel, asText(item?.hour, ''))}`.trim()),
  value: asNumber(item?.avgJoinCount, asNumber(item?.totalJoins))
}));

/**
 * Maps day-comparison response into multi-line series points.
 * @param {any} comparison
 * @returns {Array<{hourLabel:string,today:number,lastWeek:number,rollingAvg:number}>}
 */
export const toDayComparisonSeries = (comparison) => (comparison?.hourlyBreakdown || []).map((item) => ({
  hourLabel: asText(item?.hourLabel, String(item?.hour ?? '')),
  today: asNumber(item?.todayCount),
  lastWeek: asNumber(item?.lastWeekCount),
  rollingAvg: asNumber(item?.rollingFourWeekAverage)
}));

/**
 * Maps abandonment wait histogram into bar series.
 * @param {any} summary
 * @returns {Array<{bucket:string,count:number}>}
 */
export const toAbandonmentHistogramSeries = (summary) => (summary?.waitHistogram || []).map((row) => ({
  bucket: asText(row?.label),
  count: asNumber(row?.count)
}));

/**
 * Maps abandonment by-section response into bar series.
 * @param {any} bySection
 * @returns {Array<{sectionName:string,abandonmentCount:number}>}
 */
export const toAbandonmentBySectionSeries = (bySection) => (bySection?.sections || []).map((row) => ({
  sectionName: asText(row?.sectionName, row?.sectionUuid || 'Section'),
  abandonmentCount: asNumber(row?.abandonmentCount)
}));

/**
 * Maps abandonment trend response into dual-axis series.
 * @param {any} trend
 * @returns {Array<{label:string,abandonmentCount:number,estimatedRevenueLoss:number}>}
 */
export const toAbandonmentTrendSeries = (trend) => (trend?.points || []).map((row) => ({
  label: asText(row?.label),
  abandonmentCount: asNumber(row?.abandonmentCount),
  estimatedRevenueLoss: asNumber(row?.estimatedRevenueLoss)
}));

/**
 * Maps bottleneck section matrix for comparisons.
 * @param {any} response
 * @returns {Array<{sectionName:string,loadScore:number,avgWaitMinutes:number,avgServiceMinutes:number,throughputPerHour:number,abandonmentRate:number,imbalanceFlag:boolean,deviationFromStoreAveragePercent:number}>}
 */
export const toBottleneckSectionSeries = (response) => (response?.sections || []).map((row) => ({
  sectionName: asText(row?.sectionName, row?.sectionUuid || 'Section'),
  loadScore: asNumber(row?.loadScore),
  avgWaitMinutes: asNumber(row?.avgWaitMinutes),
  avgServiceMinutes: asNumber(row?.avgServiceMinutes),
  throughputPerHour: asNumber(row?.throughputPerHour),
  abandonmentRate: asNumber(row?.abandonmentRate),
  imbalanceFlag: Boolean(row?.imbalanceFlag),
  deviationFromStoreAveragePercent: asNumber(row?.deviationFromStoreAveragePercent)
}));

/**
 * Maps zone traffic distribution.
 * @param {any} response
 * @returns {Array<{name:string,value:number,percent:number}>}
 */
export const toZoneTrafficPieSeries = (response) => (response?.zones || []).map((row) => ({
  name: asText(row?.zoneId, 'UNASSIGNED'),
  value: asNumber(row?.totalJoins),
  percent: asNumber(row?.trafficSharePercent)
}));

/**
 * Maps offers funnel response to stages.
 * @param {any} funnel
 * @returns {Array<{stage:string,count:number}>}
 */
export const toOfferFunnelSeries = (funnel) => (funnel
  ? [
    { stage: 'Viewed', count: asNumber(funnel?.viewed) },
    { stage: 'Claimed', count: asNumber(funnel?.claimed) },
    { stage: 'Redeemed', count: asNumber(funnel?.redeemed) }
  ]
  : []
);

/**
 * Maps offer audience comparison.
 * @param {any} response
 * @returns {Array<{audience:string,engagementRate:number,redemptionRate:number}>}
 */
export const toOfferAudienceSeries = (response) => (response?.audiences || []).map((row) => ({
  audience: asText(row?.targetAudience, 'ALL').replaceAll('_', ' '),
  engagementRate: asNumber(row?.engagementRate),
  redemptionRate: asNumber(row?.redemptionRate)
}));

/**
 * Maps offer interaction distribution to pie.
 * @param {any} response
 * @returns {Array<{name:string,value:number}>}
 */
export const toOfferInteractionPieSeries = (response) => (response?.interactionDistribution || []).map((row) => ({
  name: asText(row?.label, '').replaceAll('_', ' '),
  value: asNumber(row?.count)
}));

/**
 * Maps offer engagement daily trend.
 * @param {any} response
 * @returns {Array<{label:string,claims:number,redemptions:number}>}
 */
export const toOfferEngagementTrendSeries = (response) => (response?.dailyTrend || []).map((row) => ({
  label: asText(row?.label, row?.date),
  claims: asNumber(row?.claims),
  redemptions: asNumber(row?.redemptions)
}));

/**
 * Maps offers comparison list to top offers bar series.
 * @param {any} comparison
 * @returns {Array<{label:string,redeemed:number,engagementRate:number,offerId:any}>}
 */
export const toTopOffersSeries = (comparison) => [...(comparison?.offers || [])]
  .sort((a, b) => asNumber(b?.redeemed) - asNumber(a?.redeemed))
  .slice(0, 10)
  .map((offer) => ({
    offerId: offer?.offerId,
    label: asText(offer?.title, `Offer ${offer?.offerId}`),
    redeemed: asNumber(offer?.redeemed),
    engagementRate: asNumber(offer?.engagementRate)
  }));

/**
 * Maps offers comparison list to views/claims/redemptions grouped bars.
 * @param {any} comparison
 * @returns {Array<{offerId:any,label:string,views:number,claims:number,redemptions:number,engagementRate:number,redemptionRate:number}>}
 */
export const toOfferPerformanceComparisonSeries = (comparison) => [...(comparison?.offers || [])]
  .sort((a, b) => {
    const totalB = asNumber(b?.viewed) + asNumber(b?.claimed) + asNumber(b?.redeemed);
    const totalA = asNumber(a?.viewed) + asNumber(a?.claimed) + asNumber(a?.redeemed);
    return totalB - totalA;
  })
  .slice(0, 10)
  .map((offer) => ({
    offerId: offer?.offerId,
    label: truncateLabel(asText(offer?.title, `Offer ${offer?.offerId}`)),
    views: asNumber(offer?.viewed),
    claims: asNumber(offer?.claimed),
    redemptions: asNumber(offer?.redeemed),
    engagementRate: asNumber(offer?.engagementRate),
    redemptionRate: asNumber(offer?.redemptionRate)
  }));

const truncateLabel = (value, maxLength = 18) => {
  const text = asText(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

/**
 * Maps appointment trend response.
 * @param {any} trend
 * @returns {Array<{label:string,booked:number,completed:number,noShows:number,cancellations:number,appointmentCompleted:number,walkInCompleted:number}>}
 */
export const toAppointmentTrendSeries = (trend) => (trend?.points || []).map((row) => ({
  label: asText(row?.label),
  booked: asNumber(row?.booked),
  completed: asNumber(row?.completed),
  noShows: asNumber(row?.noShows),
  cancellations: asNumber(row?.cancellations),
  appointmentCompleted: asNumber(row?.appointmentCompleted),
  walkInCompleted: asNumber(row?.walkInCompleted)
}));

/**
 * Maps appointment outcomes to pie.
 * @param {any} summary
 * @returns {Array<{name:string,value:number}>}
 */
export const toAppointmentOutcomePieSeries = (summary) => (summary?.outcomeDistribution || []).map((row) => ({
  name: asText(row?.outcome, '').replaceAll('_', ' '),
  value: asNumber(row?.count)
}));

/**
 * Maps appointment service distribution to pie.
 * @param {any} byService
 * @returns {Array<{name:string,value:number}>}
 */
export const toAppointmentServicePieSeries = (byService) => (byService?.services || []).map((row) => ({
  name: asText(row?.serviceName, row?.serviceUuid || 'Service'),
  value: asNumber(row?.bookedCount)
}));

/**
 * Maps popular slots to bar series.
 * @param {any} popularSlots
 * @returns {Array<{slotLabel:string,bookingCount:number}>}
 */
export const toPopularSlotsSeries = (popularSlots) => (popularSlots?.slots || []).map((row) => ({
  slotLabel: asText(row?.slotLabel),
  bookingCount: asNumber(row?.bookingCount)
}));

/**
 * Maps customer visit trend response.
 * @param {any} trend
 * @returns {Array<{label:string,newCustomers:number,returningCustomers:number}>}
 */
export const toCustomerVisitTrendSeries = (trend) => (trend?.points || []).map((row) => ({
  label: asText(row?.label),
  newCustomers: asNumber(row?.newCustomers),
  returningCustomers: asNumber(row?.returningCustomers)
}));

/**
 * Maps customer segments to pie.
 * @param {any} segments
 * @returns {Array<{name:string,value:number}>}
 */
export const toCustomerSegmentsPieSeries = (segments) => (segments?.segments || []).map((row) => ({
  name: asText(row?.segment),
  value: asNumber(row?.count)
}));

/**
 * Maps gap histogram to series.
 * @param {any} behavior
 * @returns {Array<{bucket:string,customerCount:number}>}
 */
export const toCustomerGapHistogramSeries = (behavior) => (behavior?.gapHistogram || []).map((row) => ({
  bucket: asText(row?.label),
  customerCount: asNumber(row?.customerCount)
}));

/**
 * Maps visit frequency buckets to series.
 * @param {any} behavior
 * @returns {Array<{label:string,customerCount:number}>}
 */
export const toVisitFrequencyBucketSeries = (behavior) => (behavior?.visitFrequencyBuckets || []).map((row) => ({
  label: asText(row?.label),
  customerCount: asNumber(row?.customerCount)
}));

/**
 * Maps staffing forecast points to series.
 * @param {any} forecast
 * @returns {Array<{label:string,predictedJoins:number,confidenceLow:number,confidenceHigh:number,actualJoins:number}>}
 */
export const toStaffingForecastSeries = (forecast) => (forecast?.points || []).map((row) => ({
  label: asText(row?.label),
  predictedJoins: asNumber(row?.predictedJoins),
  confidenceLow: asNumber(row?.confidenceLow),
  confidenceHigh: asNumber(row?.confidenceHigh),
  actualJoins: asNumber(row?.actualJoins)
}));

/**
 * Maps staffing efficiency to series.
 * @param {any} efficiency
 * @returns {Array<{entityName:string,customersServedPerHour:number,avgServiceMinutes:number,idleTimePercent:number,zoneId:string}>}
 */
export const toStaffingEfficiencySeries = (efficiency) => (efficiency?.efficiencies || []).map((row) => ({
  entityName: asText(row?.entityName, 'Section'),
  customersServedPerHour: asNumber(row?.customersServedPerHour),
  avgServiceMinutes: asNumber(row?.avgServiceMinutes),
  idleTimePercent: asNumber(row?.idleTimePercent),
  zoneId: asText(row?.zoneId, 'UNASSIGNED')
}));

/**
 * Maps staffing schedule to stacked bar inputs.
 * @param {any} schedule
 * @returns {{stacked:Array<any>,zoneKeys:string[]}}
 */
export const toStaffingScheduleStack = (schedule) => {
  const hours = schedule?.hours || [];
  const zoneKeys = new Set();
  hours.forEach((row) => (row?.zones || []).forEach((z) => zoneKeys.add(asText(z?.zoneId, 'UNASSIGNED'))));
  const keys = Array.from(zoneKeys);
  const stacked = hours.map((row) => {
    const base = { hourLabel: asText(row?.hourLabel) };
    (row?.zones || []).forEach((z) => {
      base[asText(z?.zoneId, 'UNASSIGNED')] = asNumber(z?.recommendedStaff);
    });
    return base;
  });
  return { stacked, zoneKeys: keys };
};

/**
 * Maps anomaly list to timeline rows.
 * @param {any} response
 * @returns {Array<{type:string,severity:string,triggeredAt:string,affectedEntity:string,currentValue:any,thresholdValue:any}>}
 */
export const toAnomalyTimelineSeries = (response) => (response?.anomalies || []).map((row) => ({
  type: asText(row?.type),
  severity: asText(row?.severity),
  triggeredAt: asText(row?.triggeredAt),
  affectedEntity: asText(row?.affectedEntity, ''),
  currentValue: row?.currentValue,
  thresholdValue: row?.thresholdValue
}));
